import React, {useEffect, useMemo, useState} from "react";
import {faBuilding, faClipboardList, faUsers} from "@fortawesome/free-solid-svg-icons";

// components
import {
  AgeSummary,
  CategorySummary,
  GenderSummary,
  MetricsSummary,
  RegionSummary,
  SurveySummary,
  TimeElapsedSummary
} from "../DashboardSummary";
import {LoadingView} from "../../../../designSystem/components/LoadingView";
import {ActionButton} from "../../../../designSystem/components/ActionButton";
import {StandardLayout} from "../../../../components/StandardLayout";

// types
import {Organization} from "../../../../types/Organization";
import {User} from "../../model/types";
import {ICase} from "../../../../types/Case";
import {Group} from "../../../../utils/constants";

// services
import {getOrganizations} from "../../../../services/organization";
import {getUsers} from "../../../../services/user";
import {getCases} from "../../../../services/case";

// hooks
import {useExportDashboard} from "../../hooks/useExportDashboard";

interface DashboardFeatureProps {
  userToken: string;
}

export const DashboardFeature = ({
  userToken
}: DashboardFeatureProps) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [cases, setCases] = useState<ICase[]>([]);
  const [selectedGroupGender, setSelectedGroupGender] = useState<string>('');
  const [selectedGroupAge, setSelectedGroupAge] = useState<string>('');
  const [selectedGroupRegion, setSelectedGroupRegion] = useState<string>('');
  const [selectedGroupSurvey, setSelectedGroupSurvey] = useState<string>('');
  const [selectedGroupTimeElapsed, setSelectedGroupTimeElapsed] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const tempOrganizations = await getOrganizations({token: userToken})
      const tempUsers = await getUsers({token: userToken});
      const tempCases = await getCases({token: userToken});

      setOrganizations(tempOrganizations);
      setUsers(tempUsers);
      setCases(tempCases);
    } catch(e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }

  const handleGroupSurveyChanged = (group: Group) => {
    setSelectedGroupSurvey(selectedGroupSurvey === group.name ? '' : group.name)
  }

  const handleGroupTimeElapsedChanged = (group: Group) => {
    setSelectedGroupTimeElapsed(selectedGroupTimeElapsed === group.name ? '' : group.name)
  }

  const handleGroupGenderChanged = (group: Group) => {
    setSelectedGroupGender(selectedGroupGender === group.name ? '' : group.name)
  }

  const handleGroupAgeChanged = (group: Group) => {
    setSelectedGroupAge(selectedGroupAge === group.name ? '' : group.name)
  }

  const handleGroupRegionChanged = (group: Group) => {
    setSelectedGroupRegion(selectedGroupRegion === group.name ? '' : group.name)
  }

  const organizationMapping = useMemo(() => {
    const tempMapping = {}
    for (const organization of organizations) {
      tempMapping[organization.id] = organization.name
    }
    return tempMapping;
  }, [organizations]);

  useEffect(() => {
    if (userToken) {
      fetchData()
    }
  }, [userToken])

  const { exportCasesDetail, exportCaseStatistics } = useExportDashboard({
    organizationMapping,
    cases,
    userCount: users.length,
    organizationCount: organizations.length,
  })

  if (isLoading || !userToken) {
    return <LoadingView />
  }

  return (
    <div className="w-[900px] m-auto">
      <div className="flex justify-end mb-4 gap-4">
        <ActionButton label="匯出數據報表" onClick={exportCaseStatistics} iconName="fa-file-export" variant="outline"/>
        <ActionButton label="匯出案件明細" onClick={exportCasesDetail} iconName="fa-file-export" variant="outline"/>
      </div>

      {/* Metrics Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <MetricsSummary
          title="業務組別總數"
          value={organizations.length}
          icon={faBuilding}
          iconColor="text-blue-500"
        />
        <MetricsSummary
          title="使用者總數"
          value={users.length}
          icon={faUsers}
          iconColor="text-blue-500"
        />
        <MetricsSummary
          title="總案件數"
          value={cases.length}
          icon={faClipboardList}
          iconColor="text-green-500"
        />
      </div>
      <div className="grid grid-cols-1 gap-6 mb-6">
        <SurveySummary
          cases={cases}
          selectedGroup={selectedGroupSurvey}
          onChangeSelectedGroup={handleGroupSurveyChanged}
        />
        <TimeElapsedSummary
          cases={cases}
          selectedGroup={selectedGroupTimeElapsed}
          onChangeSelectedGroup={handleGroupTimeElapsedChanged}
        />
        <CategorySummary cases={cases}/>
        <GenderSummary
          cases={cases}
          selectedGroup={selectedGroupGender}
          onChangeSelectedGroup={handleGroupGenderChanged}
        />
        <AgeSummary
          cases={cases}
          selectedGroup={selectedGroupAge}
          onChangeSelectedGroup={handleGroupAgeChanged}
        />
        <RegionSummary
          cases={cases}
          selectedGroup={selectedGroupRegion}
          onChangeSelectedGroup={handleGroupRegionChanged}
        />
      </div>
    </div>
  )
}
